import { useEffect } from "react";
import useNin from "../hooks/common/useNin";
import { TFormHookParams } from "../types/common";
import { GENDER_SELECTIONS } from "./constants";
import useBrn from "../hooks/common/useBrn";
import useTin from "../hooks/common/useTin";
import { VALIDATION_PRESETS } from "./formUtils";

export const useGetOwnerDetails = ({
  form,
  updateFormValues,
  error,
  setError,
  optional = [],
}: TFormHookParams) => {
  const {
    loader: loadNin,
    ninError: ninError,
    success: ninSuccess,
  } = useNin(form, updateFormValues);
  const {
    loader: loadBrn,
    brnError: brnError,
    success: brnSuccess,
  } = useBrn(form, updateFormValues);
  const {
    loader: loadTin,
    tinError: tinError,
    success: tinSuccess,
  } = useTin(form, updateFormValues);

  useEffect(() => {
    if (setError) {
      setError(ninError);
    }
  }, [ninError]);

  useEffect(() => {
    if (setError) {
      setError(tinError);
    }
  }, [tinError]);

  useEffect(() => {
    if (setError) {
      setError(brnError);
    }
  }, [brnError]);

  // Clear validation errors for auto-filled fields when successfully populated
  useEffect(() => {
    if (ninSuccess && setError && error) {
      // Clear errors for NIN-related fields
      const updatedError = { ...error };
      delete updatedError.first_name;
      delete updatedError.last_name;
      delete updatedError.other_name;

      const hasOtherErrors =
        Object.keys(updatedError).filter(
          (k) => k !== "notSubmit" && k !== "data",
        ).length > 0;

      setError(hasOtherErrors ? updatedError : {});
    }
  }, [ninSuccess, error, setError]);

  useEffect(() => {
    if (brnSuccess && setError && error) {
      // Clear errors for BRN-related fields
      const updatedError = { ...error };
      delete updatedError.legal_name;
      delete updatedError.business_name;

      const hasOtherErrors =
        Object.keys(updatedError).filter(
          (k) => k !== "notSubmit" && k !== "data",
        ).length > 0;

      setError(hasOtherErrors ? updatedError : {});
    }
  }, [brnSuccess, error, setError]);

  useEffect(() => {
    if (tinSuccess && setError && error) {
      // Clear errors for TIN-related fields
      const updatedError = { ...error };
      delete updatedError.taxpayer_name;
      delete updatedError.email;

      const hasOtherErrors =
        Object.keys(updatedError).filter(
          (k) => k !== "notSubmit" && k !== "data",
        ).length > 0;

      setError(hasOtherErrors ? updatedError : {});
    }
  }, [tinSuccess, error, setError]);

  return [
    // {
    //   field_name: "ownership_type",
    //   type: "select",
    //   label: "Type of Ownership",
    //   visible: form?.role_name === "Agent",
    //   options_data: [
    //     { id: "INDIVIDUAL", name: "Individual" },
    //     { id: "NON_INDIVIDUAL", name: "Non-Individual" },
    //   ],
    // },
    {
      field_name: "brn",
      type: "text",
      label: "BRN",
      description:
        "Enter the Business Registration Number exactly as it appears on the official business registration certificate.",
      props: {
        required: optional?.includes("brn") ? false : true,
        disabled: loadBrn,
        // smallWidth: true,
        loading: loadBrn,
      },
      validation: VALIDATION_PRESETS.BRN,
      visible:
        form.ownership_type === "NON_INDIVIDUAL" ||
        form?.role_name === "Merchant",
    },
    {
      field_name: "legal_name",
      type: "text",
      label: "Legal Name",
      description:
        "Enter the officially registered name of the business or organization as shown on the business registration documents.",

      props: {
        required: optional?.includes("legal_name") ? false : true,
        disabled: brnSuccess,
        // smallWidth: true,
        // wide: true,
      },
      validation: VALIDATION_PRESETS.FULL_NAME,
      visible:
        form.ownership_type === "NON_INDIVIDUAL" ||
        ["Merchant"]?.includes(form?.role_name),
    },
    {
      field_name: "tin",
      visible: form?.role_name === "Merchant",
      type: "text",
      label: "Tin",
      description:
        "Provide the Tax Identification Number issued by the revenue authority for the business.",
      props: {
        required: optional?.includes("tin") ? false : true,
        disabled: loadTin,
        // smallWidth: true,
        loading: loadTin,
      },
      validation: VALIDATION_PRESETS.TIN,
    },
    {
      field_name: "taxpayer_name",
      type: "text",
      label: "Taxpayer Name",
      description:
        "Enter the taxpayer's official full name as registered under the provided TIN.",
      props: {
        required: optional?.includes("taxpayer_name") ? false : true,
        disabled: tinSuccess,
        // smallWidth: true,
      },
      validation: VALIDATION_PRESETS.FULL_NAME,
      visible: form?.role_name === "Merchant",
    },
    {
      field_name: "nationality",
      type: "select",
      label: "Type of Nationality",
      description:
        "Select your nationality based on your official identification documents.",
      props: {
        required: optional?.includes("nationality") ? false : true,
      },
      options_data: [
        { id: "UGANDAN", name: "Ugandan" },
        { id: "NON-UGANDAN", name: "Non-Ugandan" },
      ],
      visible:
        form.ownership_type === "INDIVIDUAL" &&
        !["Merchant", "Admin"]?.includes(form?.role_name),
    },
    {
      field_name: "nin",
      type: "text",
      label: "NIN",
      description:
        "Enter your National Identification Number exactly as it appears on your National ID.",
      props: {
        required: optional?.includes("nin") ? false : true,
        disabled: loadNin,
        // smallWidth: true,
        loading: loadNin,
      },
      validation: VALIDATION_PRESETS.NIN,
      visible:
        form?.role_name === "Agent" ||
        // form?.role_name === "Staff" ||
        (form.nationality === "UGANDAN" &&
          form.ownership_type !== "NON_INDIVIDUAL"),
    },
    {
      field_name: "passport_number",
      type: "text",
      label: "Passport Number",
      description:
        "Enter the passport number exactly as it appears on the biodata page of your passport.",
      props: {
        required: optional?.includes("passport_number") ? false : true,
        // smallWidth: true,
      },
      visible: form.nationality === "NON-UGANDAN",
    },
    {
      field_name: "first_name",
      type: "text",
      label: "First Name",
      description:
        "Provide your first name exactly as it appears on your identification document.",
      props: {
        required: optional?.includes("first_name") ? false : true,
        disabled: ninSuccess,
        // smallWidth: true,
      },
      validation: VALIDATION_PRESETS.FIRST_NAME,
      visible: form?.role_name !== "Merchant",
    },
    {
      field_name: "last_name",
      type: "text",
      label: "Last Name",
      description:
        "Enter your last name or surname as indicated on your official identification documents.",
      props: {
        required: optional?.includes("last_name") ? false : true,
        disabled: ninSuccess,
        // smallWidth: true,
      },
      validation: VALIDATION_PRESETS.LAST_NAME,
      visible: form?.role_name !== "Merchant",
    },
    {
      field_name: "other_name",
      type: "text",
      label: "Other Name",
      description:
        "Enter any additional names (optional) exactly as they appear on your identification documents.",
      props: {
        required: false,
        disabled: ninSuccess,
        // smallWidth: true,
      },
      validation: VALIDATION_PRESETS.LAST_NAME,
      visible: form?.role_name !== "Merchant",
    },
    {
      field_name: "gender",
      type: "select",
      label: "Gender",
      description:
        "Select your gender as indicated on your national identification documents.",
      props: {
        required: optional?.includes("gender") ? false : true,
        // smallWidth: true,
      },
      options_data: GENDER_SELECTIONS,
      // value: form?.gender,
      visible: form?.role?.name !== "Admin",
    },
    // {
    //   field_name: "tin",
    //   type: "number",
    //   label: "TIN",
    //   props: { smallWidth: true },
    // },
    {
      field_name: "phone_number",
      type: "tel",
      label: "Phone Number",
      description:
        "Enter an active mobile number registered in your name and reachable for verification.",
      props: {
        required: optional?.includes("phone_number") ? false : true,
        // smallWidth: true,
      },
      validation: VALIDATION_PRESETS.PHONE_UG,
    },
    // {
    //   field_name: "alternative_phone_number",
    //   type: "tel",
    //   label: "Alternative Phone Number",
    //   props: {
    //     smallWidth: true,
    //   },
    // },
    {
      field_name: "email",
      type: "email",
      label: "Email",
      description:
        "Provide a valid email address that you actively use for communication and verification.",
      props: {
        required: optional?.includes("email") ? false : true,
        disabled: false,
      },
      // validation: VALIDATION_PRESETS.EMAIL,
    },
  ];
};
